import type { Canal } from '@sheafyield/dominio';

/**
 * O rascunho da folha Registrar, guardado fora do ciclo de vida do React.
 *
 * Por que isto existe: o `PortaoDaTrava` **não monta** o conteúdo do app
 * enquanto trancado — decisão certa, para não vazar nome de morador atrás da
 * tela trancada. A consequência cruzada é que qualquer ida ao segundo plano
 * com a tolerância em "Ao abrir" destruía o estado da folha: o endereço
 * digitado no campo "Onde?", o nome, a próxima visita em linguagem natural.
 * O cenário não é hipotético — é abrir o Mapas para conferir o número da
 * casa, ou atender uma ligação, no meio do preenchimento. Seis segundos de
 * Face ID depois, o campo estava em branco.
 *
 * **Por que `sessionStorage` e não a tabela `estado` do Dexie.** O rascunho
 * carrega nome e endereço de uma pessoa real, e a `sessionStorage` morre com
 * a aba: nada fica no disco depois que o app fecha, e a purga de troca de
 * conta (ADR-0006) não precisa saber dele. No Dexie ele sobreviveria à troca
 * de conta sem que ninguém tivesse pensado nisso. A contrapartida é honesta:
 * se o iOS descartar o web clip inteiro por pressão de memória, o rascunho
 * vai junto — mas aí a página recarrega do zero e a folha nem estaria
 * aberta.
 *
 * **A gravação é síncrona, a cada tecla, sem atraso.** Um `setTimeout` de
 * algumas centenas de milissegundos economizaria escritas de duzentos bytes
 * e abriria exatamente a janela que este módulo existe para fechar: o app
 * indo para o segundo plano entre a última tecla e o disparo do relógio.
 *
 * **O que este módulo não guarda: o Desfazer pendente.** Ele é um relógio de
 * seis segundos, e relógio não sobrevive a desmontagem — restaurá-lo
 * significaria oferecer "Desfazer" para uma visita registrada dez minutos
 * antes, o que é pior do que não oferecer nada. A visita em si está gravada
 * e a folha volta com o rascunho; o que se perde é a janela de arrependimento,
 * e ela se perde de propósito.
 */

const CHAVE = 'sheafyield:rascunho-registrar';

export interface Rascunho {
  onde: string;
  nome: string;
  /** Distingue grafia digitada do preenchimento automático da ficha ligada.
   * Ausente nos rascunhos anteriores: conservamos a grafia restaurada. */
  nomeEditado?: boolean;
  /** `null` é "ninguém encostou no campo": vale a última publicação usada. */
  publicacao: string | null;
  canal: Canal;
  proximaTexto: string;
  detalhes: boolean;
  /** O instante contra o qual "sábado 10h" é resolvido, em ISO — ele é
   *  fixado quando os detalhes abrem, e sem ele a pré-visualização voltaria
   *  com outra conta. `null` enquanto ninguém abriu os detalhes. */
  agora: string | null;
}

function ehRascunho(v: unknown): v is Rascunho {
  if (typeof v !== 'object' || v === null) return false;
  const r = v as Record<string, unknown>;
  return (
    typeof r['onde'] === 'string' &&
    typeof r['nome'] === 'string' &&
    (r['nomeEditado'] === undefined || typeof r['nomeEditado'] === 'boolean') &&
    (r['publicacao'] === null || typeof r['publicacao'] === 'string') &&
    typeof r['canal'] === 'string' &&
    typeof r['proximaTexto'] === 'string' &&
    typeof r['detalhes'] === 'boolean' &&
    (r['agora'] === null || typeof r['agora'] === 'string')
  );
}

/** O que estava digitado, ou `null` quando não há rascunho — inclusive
 *  quando o armazenamento não responde (janela privada, cota). Perder um
 *  rascunho é chato; quebrar a folha por causa dele seria pior. */
export function lerRascunho(): Rascunho | null {
  try {
    const bruto = sessionStorage.getItem(CHAVE);
    if (bruto === null) return null;
    const lido: unknown = JSON.parse(bruto);
    return ehRascunho(lido) ? lido : null;
  } catch {
    return null;
  }
}

export function gravarRascunho(r: Rascunho): void {
  try {
    sessionStorage.setItem(CHAVE, JSON.stringify(r));
  } catch {
    // Não poder lembrar não impede digitar.
  }
}

export function limparRascunho(): void {
  try {
    sessionStorage.removeItem(CHAVE);
  } catch {
    // idem
  }
}
